import { Record } from 'immutable';
import { AREA_TEXTS, AGE_RANGE_TEXTS, BODY_FIGURE_TEXTS, HEIGHT_RANGE_TEXTS } from '../consts';

export type ApiForumApplicationDonor = {
  user_no: number;
  name: string;
  areas: string[] | null;
  birth_date: string | null;
  blood_type: string | null;
  body_figure: string | null;
  height: number | null;
  photo_url: string | null;
  kyc: boolean;
  std: boolean;
  quality: string | null;
};

export type ApiForumApplicationForumPost = {
  id: number;
  area: string;
  status: string;
  expires_at: string | null;
  posted_at: string | null;
  description: string | null;
  blood_type: string[] | null;
  age_range: string[] | null;
  body_figure: string[] | null;
  height_range: string[] | null;
  donee_name_f: string | null;
  donee_birth_date: string | null;
};

export type ApiForumApplication = {
  id: number;
  comment: string;
  created_at: string;
  donor: ApiForumApplicationDonor;
  forum_post: ApiForumApplicationForumPost;
};

const ForumApplicationBase = Record({
  id: 0,
  comment: '',
  createdAt: '',
  donorUserNo: 0,
  donorName: '',
  donorAreas: [] as string[],
  donorBirthDate: null as string | null,
  donorBloodType: null as string | null,
  donorBodyFigure: null as string | null,
  donorHeight: null as number | null,
  donorPhotoUrl: null as string | null,
  donorKyc: false,
  donorStd: false,
  donorQuality: null as string | null,
  forumPostId: 0,
  forumPostArea: '',
  forumPostStatus: '',
  forumPostExpiresAt: null as string | null,
  forumPostPostedAt: null as string | null,
  forumPostDescription: null as string | null,
  forumPostBloodType: null as string[] | null,
  forumPostAgeRange: null as string[] | null,
  forumPostBodyFigure: null as string[] | null,
  forumPostHeightRange: null as string[] | null,
  forumPostDoneeNameF: null as string | null,
  forumPostDoneeBirthDate: null as string | null,
});

export class ForumApplication extends ForumApplicationBase {
  constructor(api?: ApiForumApplication) {
    super({
      id: api?.id,
      comment: api?.comment,
      createdAt: api?.created_at,
      donorUserNo: api?.donor?.user_no,
      donorName: api?.donor?.name,
      donorAreas: api?.donor?.areas || [],
      donorBirthDate: api?.donor?.birth_date,
      donorBloodType: api?.donor?.blood_type,
      donorBodyFigure: api?.donor?.body_figure,
      donorHeight: api?.donor?.height,
      donorPhotoUrl: api?.donor?.photo_url,
      donorKyc: api?.donor?.kyc ?? false,
      donorStd: api?.donor?.std ?? false,
      donorQuality: api?.donor?.quality ?? null,
      forumPostId: api?.forum_post?.id,
      forumPostArea: api?.forum_post?.area,
      forumPostStatus: api?.forum_post?.status,
      forumPostExpiresAt: api?.forum_post?.expires_at,
      forumPostPostedAt: api?.forum_post?.posted_at ?? null,
      forumPostDescription: api?.forum_post?.description ?? null,
      forumPostBloodType: api?.forum_post?.blood_type ?? null,
      forumPostAgeRange: api?.forum_post?.age_range ?? null,
      forumPostBodyFigure: api?.forum_post?.body_figure ?? null,
      forumPostHeightRange: api?.forum_post?.height_range ?? null,
      forumPostDoneeNameF: api?.forum_post?.donee_name_f ?? null,
      forumPostDoneeBirthDate: api?.forum_post?.donee_birth_date ?? null,
    });
  }

  readonly donorAreasText: string[] = this.donorAreas.map(v => AREA_TEXTS[v]).filter(Boolean);

  readonly donorInitialName: string = this.donorName ? this.donorName.split('').join('.') + '.' : '';

  readonly forumPostAreaText: string = AREA_TEXTS[this.forumPostArea] || this.forumPostArea;

  readonly forumPostBloodTypeChips: string[] = this.forumPostBloodType?.map(t => `${ t }型`) || [];

  readonly forumPostAgeRangeTexts: string[] = this.forumPostAgeRange?.map(k => AGE_RANGE_TEXTS[k] || k) || [];

  readonly forumPostBodyFigureTexts: string[] = this.forumPostBodyFigure?.map(k => BODY_FIGURE_TEXTS[k] || k) || [];

  readonly forumPostHeightRangeTexts: string[] = this.forumPostHeightRange?.map(k => HEIGHT_RANGE_TEXTS[k] || k) || [];

  readonly forumPostIsOpen: boolean = this.forumPostStatus === 'OPEN';

  readonly forumPostIsExpired: boolean = this.forumPostExpiresAt
    ? new Date(this.forumPostExpiresAt) < new Date()
    : false;

  readonly forumPostDaysLeft: number = this.forumPostExpiresAt
    ? Math.max(0, Math.round(
        (new Date(this.forumPostExpiresAt.slice(0, 10)).getTime() -
         Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())) /
        86400000
      ))
    : 0;

  readonly forumPostDoneeAgeText: string = (() => {
    if (!this.forumPostDoneeBirthDate) return '';
    const age = Math.floor((Date.now() - new Date(this.forumPostDoneeBirthDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
    return `${ Math.floor(age / 10) * 10 }代`;
  })();

  readonly createdAtText: string = (() => {
    if (!this.createdAt) return '';
    const d = new Date(this.createdAt);
    return `${ d.getFullYear() }/${ String(d.getMonth() + 1).padStart(2, '0') }/${ String(d.getDate()).padStart(2, '0') } ${ String(d.getHours()).padStart(2, '0') }:${ String(d.getMinutes()).padStart(2, '0') }`;
  })();

  readonly forumPostPostedAtText: string = (() => {
    if (!this.forumPostPostedAt) return '';
    const d = new Date(this.forumPostPostedAt);
    return `${ d.getFullYear() }/${ String(d.getMonth() + 1).padStart(2, '0') }/${ String(d.getDate()).padStart(2, '0') }`;
  })();

  readonly forumPostExpiresAtText: string = (() => {
    if (!this.forumPostExpiresAt) return '';
    const d = new Date(this.forumPostExpiresAt);
    return `${ d.getFullYear() }/${ String(d.getMonth() + 1).padStart(2, '0') }/${ String(d.getDate()).padStart(2, '0') }`;
  })();
}
